/**
 * Badge artwork from the loyalty API: where it is kept between page loads, and which picture a
 * badge draws.
 *
 * Both halves are the HOST's, by the library's design (`@drewling/urc-loyalty-api` and
 * `@drewling/urc-loyalty-ui` 0.19.0). The client owns the protocol -- when to ask for artwork, and
 * validating whatever a store hands back (snapshot version, API origin, every URL) -- and a surface
 * draws whatever `resolveBadgeIcon` answers. Ported from urc-uxlib's reference host,
 * `apps/demo/src/badgeArtworkStore.ts` and `apps/demo/src/badgeIcons.ts`.
 *
 * **Why a store at all.** The artwork rides on `GET /api/badges?include=image,locked_image`, which
 * measured ~1 MB on 2026-09-28, served uncompressed even to a gzip/br request and with no
 * Cache-Control -- against ~5 KB for the plain catalogue. Without a store every badge read on every
 * page (the profile block, the modal, the toast reconcile) would pay that. With one, a browser pays
 * it once per `ARTWORK_TTL_MS`.
 *
 * **Why IndexedDB.** The value is ~1 MB; localStorage's whole quota is typically 5 MB, it blocks the
 * main thread, and sessionStorage would re-download on every tab. Every IndexedDB call is guarded:
 * it is unavailable in some private modes and can be blocked by another tab's upgrade, and either
 * way the store degrades to an in-memory map for the page -- still one artwork download per page
 * rather than one per read. A read that never settles is bounded by the client's own `timeoutMs`.
 *
 * **Not fan data, so not cleared on an account switch.** The artwork is the programme's: one
 * snapshot serves every fan of a tenant, and the client refuses a snapshot fetched from any other
 * `baseUrl`. See D-095 and `perFanStorage.test.js`.
 *
 * **Nothing is imported here**, which is what lets badgeArtwork.test.js drive it under `node --test`.
 */

/**
 * How long a stored snapshot is trusted.
 *
 * The API offers no version signal for its artwork, so a TTL is the only way a re-uploaded picture,
 * or a badge added after the snapshot was taken (it draws the built-in glyph meanwhile), ever shows
 * up. A day is the reference host's choice, not a measurement.
 */
export const ARTWORK_TTL_MS = 24 * 60 * 60 * 1000;

/** The IndexedDB database. Named in docs/subsystems/gutenberg-blocks.md, *Badge artwork*. */
export const ARTWORK_DB = 'urc.loyalty.badgeArtwork';

/** The one object store inside it, keyed by the API `baseUrl` the snapshot belongs to. */
const OBJECT_STORE = 'snapshots';

/**
 * Wrap a snapshot for storage at `now`.
 *
 * @param {Object} snapshot The client's `BadgeArtworkSnapshot`.
 * @param {number} now      Epoch milliseconds.
 * @return {{savedAt: number, snapshot: Object}} What is actually stored.
 */
export function envelope( snapshot, now ) {
	return { savedAt: now, snapshot };
}

/**
 * The snapshot a stored value still vouches for at `now`, or undefined.
 *
 * Undefined once it is `ttlMs` old -- or stamped in the future, which only a clock moved backwards
 * produces, and which would otherwise pin a snapshot for as long as the skew. The snapshot itself is
 * NOT checked here: the client validates whatever `read()` answers, so this needs no copy of its
 * rules.
 *
 * @param {*}      raw   Whatever storage held.
 * @param {number} now   Epoch milliseconds.
 * @param {number} ttlMs How long a snapshot is trusted.
 * @return {Object|undefined} The snapshot, or undefined for a miss.
 */
export function openEnvelope( raw, now, ttlMs ) {
	if ( typeof raw !== 'object' || raw === null ) {
		return undefined;
	}

	const { savedAt, snapshot } = raw;

	if ( typeof savedAt !== 'number' || savedAt > now || now - savedAt >= ttlMs ) {
		return undefined;
	}

	return snapshot;
}

/**
 * A `BadgeArtworkStore` over IndexedDB, falling back to memory for the page.
 *
 * @param {Object}       options
 * @param {string}       options.key Which environment the snapshot belongs to -- the client's `baseUrl`.
 * @param {() => number} options.now The clock the TTL reads.
 * @return {{read: () => Promise<Object|undefined>, write: (snapshot: Object) => Promise<void>}} The store.
 */
export function createBadgeArtworkStore( { key, now } ) {
	const memory = new Map();
	let opened;

	// Opened lazily, once: a page that never reads badges never touches IndexedDB.
	const database = () => {
		if ( opened === undefined ) {
			opened = openDatabase();
		}

		return opened;
	};

	return {
		async read() {
			const db = await database();
			const raw =
				db === null
					? memory.get( key )
					: await run( db, 'readonly', store => store.get( key ) );

			return openEnvelope( raw, now(), ARTWORK_TTL_MS );
		},
		async write( snapshot ) {
			const entry = envelope( snapshot, now() );
			const db = await database();

			if ( db === null ) {
				memory.set( key, entry );

				return;
			}

			await run( db, 'readwrite', store => store.put( entry, key ) );
		},
	};
}

/**
 * The database, or null wherever IndexedDB cannot be had -- never a rejection.
 *
 * @return {Promise<IDBDatabase|null>} The open database.
 */
function openDatabase() {
	if ( typeof indexedDB === 'undefined' ) {
		return Promise.resolve( null );
	}

	return new Promise( resolve => {
		try {
			const request = indexedDB.open( ARTWORK_DB, 1 );

			request.onupgradeneeded = () => request.result.createObjectStore( OBJECT_STORE );
			request.onsuccess = () => {
				// Step aside for a later schema in another tab rather than blocking its upgrade. A
				// closed connection makes every later run() resolve undefined, i.e. a miss.
				request.result.onversionchange = () => request.result.close();
				resolve( request.result );
			};
			request.onerror = () => resolve( null );
			request.onblocked = () => resolve( null );
		} catch ( error ) {
			resolve( null );
		}
	} );
}

/**
 * One request in its own transaction. A failure resolves undefined rather than rejecting.
 *
 * @param {IDBDatabase}                           db   The database.
 * @param {string}                                mode 'readonly' or 'readwrite'.
 * @param {(store: IDBObjectStore) => IDBRequest} op   The request to make.
 * @return {Promise<*>} The request's result, or undefined.
 */
function run( db, mode, op ) {
	return new Promise( resolve => {
		try {
			const request = op( db.transaction( OBJECT_STORE, mode ).objectStore( OBJECT_STORE ) );

			request.onsuccess = () => resolve( request.result );
			request.onerror = () => resolve( undefined );
		} catch ( error ) {
			resolve( undefined );
		}
	} );
}

/**
 * Which picture a badge draws, in every slot (`'block' | 'modal' | 'toast'`).
 *
 * - An EARNED badge draws its earned artwork, `imageUrl`.
 * - Any other badge -- locked, and in-progress, which the fan has not earned either -- draws its
 *   LOCKED artwork, `lockedImageUrl`. Never the earned picture, even when that is all the API has
 *   for it: showing a fan the art of a badge they have not earned reads as having earned it.
 * - No artwork for that state: null, and the surface keeps the library's built-in glyph. The dev
 *   catalogue's `test` badge has no locked picture, so a fan who has not earned it sees the glyph.
 *
 * @param {Object} badge A `BadgeDetail` from `getBadgesDetail()`.
 * @return {string|null} A `data:image/…` URI, or null for the glyph.
 */
export function resolveBadgeIcon( badge ) {
	const url = badge.status === 'earned' ? badge.imageUrl : badge.lockedImageUrl;

	return typeof url === 'string' && url !== '' ? url : null;
}
